import { appData } from '@/lib/data';
import { getQueryString, pushParamsToUrl } from '@/lib/util';
import {
  useGetTenantCategoryListQuery,
  useUpdateTenantCategoryAccessMutation,
} from '@/modules/services';
import { Col, DataTable, Row, Search, Spinner, Toggle } from 'elmo-elements';
import React, { useEffect, useRef, useState } from 'react';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useUserHasRole } from '@/lib/auth/useUserHasRoles';
import { Role } from '@/lib/auth/roles/role.enum';
import MyLibraryRoutes from '@/pages/MyLibrary/MyLibraryRoutes';
import { TenantCategoryType } from '@/modules/types/tenant';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useDispatch } from 'react-redux';

const columns = [
  {
    title: 'Category name',
  },
  {
    title: '',
  },
];

const pageSizes = [10, 20, 50, 100];

interface UpdatedCategoryState {
  newValue: boolean;
  loading: boolean;
}

const CategoriesComponentV2 = (): React.ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const hasAdminRole = useUserHasRole([Role.Admin, Role.Super, Role.Migration]);
  const dispatch = useDispatch();

  const currentQueryString = getQueryString(
    location.search,
    appData.pagination.defaultParams,
  );

  const [searchKeyword, setSearchKeyword] = useState<string>(
    currentQueryString.search ?? '',
  );
  const [currentPage, setCurrentPage] = useState(currentQueryString.page);
  const [pageSize, setPageSize] = useState(
    pageSizes.includes(currentQueryString.limit)
      ? currentQueryString.limit
      : pageSizes[0],
  );

  const [updatedCategories, setUpdatedCategories] = useState<
    Record<string, UpdatedCategoryState>
  >({});

  const { data: categoriesData, isLoading: isCategoriesDataLoading } =
    useGetTenantCategoryListQuery(currentQueryString);

  const [updateTenantCategoryAccess] = useUpdateTenantCategoryAccessMutation();

  // use to prevent state update on unmounted component
  const isMounted = useRef(true);

  const onToggle = async (category: TenantCategoryType) => {
    setUpdatedCategories((state) => ({
      ...state,
      [category.id]: {
        newValue: !category.is_selected,
        loading: true,
      },
    }));

    let succeed = false;

    try {
      await updateTenantCategoryAccess({
        ...category,
        is_selected: !category.is_selected,
      }).unwrap();
      succeed = true;
    } catch (e) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: `Failed to ${
            !category.is_selected ? 'enable' : 'disable'
          } tenant category access.`,
        },
      });
    }

    // added delay to prevent spam toggling too quickly
    setTimeout(() => {
      if (!isMounted.current) return; // prevent state update on unmounted

      if (!succeed) {
        setUpdatedCategories((prevState) => {
          const newState = { ...prevState };
          delete newState[category.id];
          return newState;
        });
      } else {
        setUpdatedCategories((state) => ({
          ...state,
          [category.id]: {
            ...state[category.id],
            loading: false,
          },
        }));
      }
    }, 1000);
  };

  // cleanup
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  // removed tracked categories on page change
  useEffect(() => {
    setUpdatedCategories({});
  }, [currentPage]);

  const handleSearchChange = (searchValue: string) => {
    setSearchKeyword(searchValue);
    if (searchValue === '')
      pushParamsToUrl({ search: '', page: 1, limit: pageSize }, history);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    pushParamsToUrl({ page: pageNumber }, history);
  };

  const handlSearch = (search: string) => {
    pushParamsToUrl(
      {
        search,
        page: 1,
        limit: pageSize,
      },
      history,
    );
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    pushParamsToUrl(
      {
        search: searchKeyword,
        page: 1,
        limit: size,
      },
      history,
    );
  };

  if (isCategoriesDataLoading) return <Spinner />;

  if (hasAdminRole !== undefined && !hasAdminRole) {
    history.replace(generatePath(MyLibraryRoutes.path));
    return <Spinner />;
  }

  const checkCategoryAccessIsUpdating = (category: TenantCategoryType) =>
    updatedCategories[category.id]?.loading ?? false;
  const getCategorySelectionValue = (category: TenantCategoryType) =>
    updatedCategories[category.id]?.newValue ?? category.is_selected;

  return (
    <div id="Categories">
      <Row className="categories-header">
        <Col span={12}>
          <Search
            value={searchKeyword}
            isVisible
            onChange={(value) => handleSearchChange(value)}
            onSubmit={() => handlSearch(searchKeyword)}
            isSubmitEnabledAlways={true}
          />
        </Col>
      </Row>
      <DataTable
        className="categories-table"
        isLoading={isCategoriesDataLoading}
      >
        <DataTable.Header columns={columns} />
        <DataTable.Body>
          {categoriesData?.data?.map((category: TenantCategoryType) => (
            <DataTable.Tr key={category.id}>
              <DataTable.Td>{category.name}</DataTable.Td>
              <DataTable.Td className="toggle-wrapper">
                <Toggle
                  disabled={checkCategoryAccessIsUpdating(category)}
                  onClick={() => {}} // @Deprecated
                  onChange={() => {
                    onToggle(category);
                  }}
                  isOn={getCategorySelectionValue(category)}
                />
                <span
                  className={
                    checkCategoryAccessIsUpdating(category) ? 'loading' : ''
                  }
                >
                  {checkCategoryAccessIsUpdating(category)
                    ? getCategorySelectionValue(category)
                      ? 'Enabling'
                      : 'Disabling'
                    : getCategorySelectionValue(category)
                    ? 'Enabled'
                    : 'Disabled'}
                </span>
              </DataTable.Td>
            </DataTable.Tr>
          ))}
        </DataTable.Body>
        <DataTable.Pagination
          pageSize={pageSize}
          pageSizeOptions={pageSizes}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onPageSizeChange={(size) => handlePageSizeChange(size)}
          totalResults={categoriesData?.meta.itemCount ?? 0}
        />
      </DataTable>
    </div>
  );
};

export default CategoriesComponentV2;
